import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {App, Button, Card, Modal, Segmented, Space, Spin, Tooltip} from 'antd';
import {
    FullscreenOutlined,
    LineChartOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import {
    CompleteChartData,
    MarkPriceCandle,
    TechnicalIndicatorData,
    tradingService
} from '../../../services/tradingService';
import {IndicatorConfig, TechnicalIndicator, useChartState} from '../../../hooks/useChartState';
import {useKlineTimer} from '../../../hooks/useKlineTimer';
import CompactIndicatorBar from './CompactIndicatorBar';
import QuickTradingPanel from './QuickTradingPanel';
import LightweightCandlestickChart, {
    KLineBar,
    TechnicalIndicatorData as ChartIndicatorData
} from '../../../components/charts/LightweightCandlestickChart';
import './ChartContainer.css';
import './ChartModalEnhanced.css';


// 时间周期选项（按照需求配置）
const TIME_PERIODS = [
    {label: '1m', value: '1m', limit: 240},
    {label: '5m', value: '5m', limit: 240},
    {label: '1h', value: '1h', limit: 240},
    {label: '4h', value: '4h', limit: 60},
    {label: '1d', value: '1d', limit: 150},
];

// K线采样选项
const KLINE_SAMPLE_OPTIONS = [
    {label: '30', value: 30},
    {label: '60', value: 60},
    {label: '120', value: 120},
    {label: '180', value: 180},
    {label: '240', value: 240}
];

/**
 * 格式化时间帧显示 - 将value转换为label显示格式
 */
const formatTimeframeDisplay = (timeframeValue: string): string => {
    const period = TIME_PERIODS.find(p => p.value === timeframeValue);
    return period ? period.label : timeframeValue;
};


interface ChartContainerProps {
    instId: string;         // 合约ID
    apiKeyId: number;       // API Key ID
    defaultPeriod?: string; // 默认时间周期
    height?: number;        // 图表高度
    limit?: number;         // 数据条数限制
    markPrice?: number | null; // 标记价格
    fourHourAvgChange?: number | null; // 4小时平均涨跌幅
    readOnly?: boolean;
}

/**
 * 全屏图表模态框组件
 */
const FullscreenChartModal: React.FC<{
    visible: boolean;
    onClose: () => void;
    instId: string;
    apiKeyId: number;
    data: KLineBar[];
    loading: boolean;
    _error: string | null;
    timeFrame: string;
    klineLimit: number;
    selectedIndicators: TechnicalIndicator[];
    indicatorConfigs: Record<TechnicalIndicator, IndicatorConfig>;
    convertedIndicators: Record<string, ChartIndicatorData[]>;
    visibleIndicators: Record<string, boolean>;
    markPrice?: number | null;
    currentMarkPrice?: number | null; // 从getCompleteChartData获取的当前标记价格
    fourHourAvgChange?: number | null; // 4小时平均涨跌幅
    readOnly?: boolean;
    // 定时器状态相关
    isTimerActive: boolean;
    nextUpdateTime: Date | null;
    timeUntilNextUpdate: number;
    onToggleTimer: () => void;
    // 格式化函数
    formatTimeframeDisplay: (timeframeValue: string) => string;
    formatCountdown: (ms: number) => string;
    onPeriodChange: (timeFrame: string) => void;
    onKlineLimitChange: (klineLimit: number) => void;
    onToggleIndicator: (indicator: TechnicalIndicator) => void;
    onUpdateIndicatorConfig: (indicator: TechnicalIndicator, config: Partial<IndicatorConfig>) => void;
    onActivateIndicator?: (indicator: TechnicalIndicator) => void; // 新增：激活指标函数
    onClearAllIndicators?: () => void; // 新增：清除所有指标函数
}> = ({
          visible,
          onClose,
          instId,
          apiKeyId,
          data,
          loading,
          timeFrame,
          klineLimit,
          selectedIndicators,
          indicatorConfigs,
          convertedIndicators,
          visibleIndicators,
          markPrice,
          currentMarkPrice,
          fourHourAvgChange,
          // 定时器状态
          isTimerActive,
          nextUpdateTime,
          timeUntilNextUpdate,
          onToggleTimer,
          // 格式化函数
          formatTimeframeDisplay,
          formatCountdown,
          onPeriodChange,
          onKlineLimitChange,
          onToggleIndicator,
          onUpdateIndicatorConfig,
          onActivateIndicator,
          onClearAllIndicators,
          readOnly = false
      }) => {
    const {message: messageApi} = App.useApp();
    // 动态尺寸计算
    const controlsRef = useRef<HTMLDivElement>(null);
    const [controlsHeight, setControlsHeight] = useState(0);
    const [chartDimensions, setChartDimensions] = useState({width: 0, height: 0});

    const displayPrice = currentMarkPrice || markPrice;

    // 涨跌幅 = 最新一根K线 (close - open) / open
    const priceChangePercent = useMemo(() => {
        if (!data || data.length < 1) return null;
        const last = data[data.length - 1];
        if (last == null || last.open == null || last.open === 0) return null;
        return ((last.close - last.open) / last.open) * 100;
    }, [data]);

    // 最新K线方向（决定价格颜色）
    const isPriceUp = useMemo(() => {
        if (!data || data.length < 1) return true;
        const last = data[data.length - 1];
        return last.close >= last.open;
    }, [data]);

    // 计算模态框尺寸和布局 - 优化响应式断点，确保小屏幕下布局稳定
    const getModalDimensions = useCallback(() => {
        const vh = window.innerHeight;
        const vw = window.innerWidth;

        // 优化的响应式尺寸设置 - 五个断点确保平滑过渡
        let modalHeight, modalWidth;

        if (vw >= 1200) {
            // 大屏幕：保留屏幕空间，保持舒适观看体验
            modalHeight = vh > 1200 ? '85vh' : '90vh';
            modalWidth = Math.min(vw * 0.95, 1600);
        } else if (vw >= 1024) {
            // 大平板：适配中等分辨率，保持比例协调
            modalHeight = vh > 1000 ? '88vh' : '92vh';
            modalWidth = Math.min(vw * 0.98, 1400);
        } else if (vw >= 768) {
            // 小平板：紧凑布局，最大化利用空间
            modalHeight = '90vh';
            modalWidth = vw * 0.98;
        } else if (vw >= 576) {
            // 手机横屏：优化高度，为界面元素预留空间
            modalHeight = '88vh';
            modalWidth = '100vw';
        } else {
            // 手机竖屏：增加高度以适应6行交易面板布局
            modalHeight = '92vh'; // 从85vh增加到92vh
            modalWidth = '100vw';
        }

        return {height: modalHeight, width: modalWidth};
    }, []);

    // 获取响应式布局类型 - 720px+水平布局，720px-垂直布局
    const getLayoutType = useCallback(() => {
        const vw = window.innerWidth;
        if (vw >= 1200) return 'new-layout';     // 大屏幕 - 水平布局
        if (vw >= 1024) return 'new-layout';     // 大平板 - 水平布局
        if (vw >= 720) return 'new-layout';      // 小平板和手机横屏 - 水平布局
        return 'single-column';                  // 720px以下 - 垂直布局
    }, []);

    // 获取布局样式
    const getLayoutStyle = useCallback(() => {
        const layoutType = getLayoutType();
        return {
            containerClass: layoutType === 'single-column' ? 'chart-modal-container single-column' : 'chart-modal-container'
        };
    }, [getLayoutType]);

    // 动态计算图表尺寸 - 优化所有分辨率下的图表空间分配
    const calculateChartDimensions = useCallback(() => {
        if (!visible) return;

        const modalDims = getModalDimensions();
        const modalWidth = typeof modalDims.width === 'string' ?
            (modalDims.width.endsWith('vw') ?
                (parseFloat(modalDims.width) * window.innerWidth / 100) :
                parseFloat(modalDims.width)) :
            modalDims.width;
        const modalHeight = typeof modalDims.height === 'string' ?
            (modalDims.height.endsWith('vh') ?
                (parseFloat(modalDims.height) * window.innerHeight / 100) :
                parseFloat(modalDims.height)) :
            modalDims.height;

        const vw = window.innerWidth;
        const gap = 12; // 网格间距

        // 根据断点系统精确计算
        let topBarHeight, tradingBarHeight, minChartWidth, minChartHeight;

        if (vw >= 1200) {
            // 大屏幕：舒适的控件高度
            topBarHeight = 80;
            tradingBarHeight = 100;
            minChartWidth = 600;
            minChartHeight = 400;
        } else if (vw >= 1024) {
            // 大平板：适中尺寸
            topBarHeight = 75;
            tradingBarHeight = 90;
            minChartWidth = 500;
            minChartHeight = 350;
        } else if (vw >= 768) {
            // 小平板：紧凑布局
            topBarHeight = 70;
            tradingBarHeight = 80;
            minChartWidth = 400;
            minChartHeight = 300;
        } else if (vw >= 576) {
            // 手机横屏：优化高度利用
            topBarHeight = 65;
            tradingBarHeight = 70;
            minChartWidth = 350;
            minChartHeight = 280;
        } else if (vw >= 480) {
            // 手机竖屏：最小可用尺寸，与CSS保持一致
            topBarHeight = 60;
            tradingBarHeight = 65;
            minChartWidth = 300;
            minChartHeight = 250;
        } else {
            // 超小屏幕特殊优化
            topBarHeight = 55;
            tradingBarHeight = 60;
            minChartWidth = 280;
            minChartHeight = 220;
        }

        // 计算可用空间
        const availableChartHeight = modalHeight - topBarHeight - tradingBarHeight - gap * 2 - 24; // 24为总padding
        const chartWidth = modalWidth - 24; // 左右padding

        setChartDimensions({
            width: Math.max(minChartWidth, chartWidth),
            height: Math.max(minChartHeight, availableChartHeight)
        });
    }, [visible, getModalDimensions]);

    // 监听控制面板高度变化
    useEffect(() => {
        if (!visible || !controlsRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const {height} = entry.contentRect;
                setControlsHeight(height);
            }
        });

        resizeObserver.observe(controlsRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [visible]);

    // 窗口尺寸变化时重新计算 - 移除函数依赖，使用空依赖数组
    useEffect(() => {
        const handleResize = () => {
            calculateChartDimensions();
            // 触发布局重新渲染
            const layoutType = getLayoutType();
            // console.log(`[FullscreenChartModal] 窗口尺寸变化，布局类型: ${layoutType}`, {
            //     width: window.innerWidth,
            //     height: window.innerHeight,
            //     modalDimensions: getModalDimensions()
            // });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []); // 移除函数依赖，避免无限循环

    // 控制面板高度变化时重新计算图表尺寸
    useEffect(() => {
        calculateChartDimensions();
    }, [controlsHeight, calculateChartDimensions]);
    // ESC键关闭支持
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && visible) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [visible, onClose]);

    // 全屏模式复用父组件的价格更新机制，避免重复定时器
    // 价格数据通过currentMarkPrice状态在父组件中统一管理


    return (
        <Modal
            title={
                <div className="modal-header-row">
                    <span className="header-name-block">
                        <LineChartOutlined/>
                        <span style={{fontWeight: 600, whiteSpace: 'nowrap'}}>{instId}</span>
                    </span>
                    <Segmented
                        size="small"
                        value={timeFrame}
                        options={TIME_PERIODS.map(periodItem => ({
                            label: periodItem.label,
                            value: periodItem.value,
                            disabled: periodItem.value === timeFrame
                        }))}
                        onChange={(value) => onPeriodChange(value as string)}
                    />
                    <Segmented
                        size="small"
                        value={klineLimit}
                        options={KLINE_SAMPLE_OPTIONS.map(limitOption => ({
                            label: limitOption.label,
                            value: limitOption.value,
                            disabled: limitOption.value === klineLimit
                        }))}
                        onChange={(value) => onKlineLimitChange(value as number)}
                    />
                    <CompactIndicatorBar
                        selectedIndicators={selectedIndicators}
                        indicatorConfigs={indicatorConfigs}
                        onToggleIndicator={onToggleIndicator}
                        onUpdateIndicatorConfig={onUpdateIndicatorConfig}
                        onClearAllIndicators={onClearAllIndicators}
                    />
                    <div className="modal-status-badge">
                        <span
                            className={`status-dot ${isTimerActive ? 'active' : 'inactive'}`}
                            onClick={onToggleTimer}
                            title={isTimerActive ? '定时更新已启用' : '定时更新已暂停'}
                        />
                        <span className="status-countdown">
                            {formatCountdown(timeUntilNextUpdate)}
                        </span>
                    </div>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={getModalDimensions().width}
            style={{
                top: 20,
                left: 20,
                margin: 0,
                maxWidth: 'none'
            }}
            styles={{
                header: {
                    backgroundColor: 'rgba(31, 31, 31, 0.95)',
                    border: 'none',
                    color: '#fff',
                    backdropFilter: 'blur(12px)',
                    padding: '4px 12px',
                    height: 'auto',
                    minHeight: '40px'
                },
                body: {
                    padding: '16px',
                    backgroundColor: 'var(--pt-bg-panel)',
                    height: getModalDimensions().height,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                },
                content: {
                    backgroundColor: 'transparent',
                    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.1)'
                }
            }}
            maskClosable={true}
            centered={false}
        >
            <div className={getLayoutStyle().containerClass}>
                {getLayoutType() === 'new-layout' ? (
                    // 新布局
                    <>
                        <div className="bottom-section">
                            {/* K线图区域 (4/5宽度) */}
                            <div className="chart-area">
                                <div className="chart-price-overlay">
                                    <span className={`price-value ${isPriceUp ? 'up' : 'down'}`}>
                                        {displayPrice != null ? displayPrice.toFixed(2) : '--'}
                                    </span>
                                    {priceChangePercent != null && (
                                        <span className={`price-change ${priceChangePercent >= 0 ? 'up' : 'down'}`}>
                                            {priceChangePercent >= 0 ? '+' : ''}{priceChangePercent.toFixed(2)}%
                                        </span>
                                    )}
                                </div>
                                <div style={{width: '100%', flex: 1, minHeight: 0}}>
                                    <LightweightCandlestickChart
                                        data={data}
                                        loading={loading}
                                        markPrice={currentMarkPrice || markPrice || undefined}
                                        timeFrame={timeFrame as any}
                                        indicators={convertedIndicators}
                                        visibleIndicators={visibleIndicators}
                                        maxVisibleBars={klineLimit}
                                    />
                                </div>
                            </div>

                            {/* 快捷交易面板区域 (1/5宽度) */}
                            <div className="quick-trading-area-wrapper">
                                <QuickTradingPanel
                                    apiKeyId={apiKeyId}
                                    currentPrice={currentMarkPrice || markPrice || undefined}
                                    instId={instId}
                                    fourHourAvgChange={fourHourAvgChange || undefined}
                                    readOnly={readOnly}
                                    onTradeSuccess={() => {
                                        messageApi.success('交易成功！');
                                        // 可以在这里添加其他成功回调逻辑
                                    }}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    // 单栏布局（移动端）
                    <>
                        <div className="chart-area"
                             style={{marginBottom: '12px', height: 'auto', flex: 1, minHeight: 300}}>
                            <div className="chart-price-overlay">
                                <span className={`price-value ${isPriceUp ? 'up' : 'down'}`}>
                                    {displayPrice != null ? displayPrice.toFixed(2) : '--'}
                                </span>
                                {priceChangePercent != null && (
                                    <span className={`price-change ${priceChangePercent >= 0 ? 'up' : 'down'}`}>
                                        {priceChangePercent >= 0 ? '+' : ''}{priceChangePercent.toFixed(2)}%
                                    </span>
                                )}
                            </div>
                            <div style={{width: '100%', flex: 1, minHeight: 0}}>
                                <LightweightCandlestickChart
                                    data={data}
                                    loading={loading}
                                    markPrice={currentMarkPrice || markPrice || undefined}
                                    timeFrame={timeFrame as any}
                                    indicators={convertedIndicators}
                                    visibleIndicators={visibleIndicators}
                                    maxVisibleBars={klineLimit}
                                />
                            </div>
                        </div>

                        <QuickTradingPanel
                            apiKeyId={apiKeyId}
                            currentPrice={currentMarkPrice || markPrice || undefined}
                            instId={instId}
                            fourHourAvgChange={fourHourAvgChange || undefined}
                            readOnly={readOnly}
                            onTradeSuccess={() => {
                                messageApi.success('交易成功！');
                            }}
                        />
                    </>
                )}
            </div>
        </Modal>
    );
};

const ChartContainer: React.FC<ChartContainerProps> = ({
                                                           instId,
                                                           apiKeyId,
                                                           height = 400,
                                                           markPrice,
                                                           fourHourAvgChange,
                                                           readOnly = false
                                                       }) => {
    const {message: messageApi} = App.useApp();
    const [data, setData] = useState<KLineBar[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [containerWidth, setContainerWidth] = useState(800);
    const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

    // 使用统一的状态管理Hook
    const {
        timeFrame,
        klineLimit,
        selectedIndicators,
        indicatorConfigs,
        updateTimeFrame,
        updateKlineLimit,
        toggleIndicator,
        updateIndicatorConfig,
        clearAllIndicators
    } = useChartState(instId);

    // 技术指标数据
    const [indicatorData, setIndicatorData] = useState<Record<string, TechnicalIndicatorData>>({});

    // 当前标记价格（从getCompleteChartData获取）
    const [currentMarkPrice, setCurrentMarkPrice] = useState<number | null>(null);

    // 价格+涨跌幅（主页面图表覆盖层）
    const mainDisplayPrice = currentMarkPrice || markPrice;

    // 涨跌幅 = 最新一根K线 (close - open) / open
    const mainPriceChangePercent = useMemo(() => {
        if (!data || data.length < 1) return null;
        const last = data[data.length - 1];
        if (last == null || last.open == null || last.open === 0) return null;
        return ((last.close - last.open) / last.open) * 100;
    }, [data]);

    // 最新K线方向（决定价格颜色）
    const mainIsPriceUp = useMemo(() => {
        if (!data || data.length < 1) return true;
        const last = data[data.length - 1];
        return last.close >= last.open;
    }, [data]);

    /**
     * 将后端返回的指标数据转换为 LightweightCandlestickChart 需要的格式
     * 后端格式: { EMA: { success: true, data: { metricName: 'EMA', values: [{timestamp, multiPeriodValues: {ema_5: 123}}] } } }
     * 新组件格式: { EMA_ema_12: [{ time, value }, ...], EMA_ema_26: [...] }
     */
    const convertIndicatorData = useCallback((indicators: Record<string, TechnicalIndicatorData>): Record<string, ChartIndicatorData[]> => {
        const result: Record<string, ChartIndicatorData[]> = {};

        Object.entries(indicators).forEach(([indicatorName, indicatorResponse]) => {
            // 跳过无效数据
            if (!indicatorResponse || !indicatorResponse.data) return;

            const {metricName, values} = indicatorResponse.data as any;

            // 处理多周期指标数据 - 后端格式: values: [{timestamp, multiPeriodValues: {ema_5: 123, ema_20: 456}}]
            if (values && Array.isArray(values) && values.length > 0) {
                // 检查第一个元素是否有 multiPeriodValues
                const firstValue = values[0];
                if (firstValue.multiPeriodValues && typeof firstValue.multiPeriodValues === 'object') {
                    // 收集所有周期 key
                    const periodKeys = new Set<string>();
                    values.forEach((v: any) => {
                        if (v.multiPeriodValues) {
                            Object.keys(v.multiPeriodValues).forEach(key => periodKeys.add(key));
                        }
                    });

                    // 为每个周期创建数据数组
                    periodKeys.forEach(periodKey => {
                        const key = `${metricName}_${periodKey}`;
                        const periodData: ChartIndicatorData[] = [];

                        values.forEach((v: any) => {
                            const timestamp = v.timestamp || v.time;
                            const periodValue = v.multiPeriodValues?.[periodKey];

                            if (periodValue !== undefined && periodValue !== null) {
                                // 处理简单数值
                                if (typeof periodValue === 'number') {
                                    periodData.push({time: timestamp, value: periodValue});
                                }
                                // 处理复杂对象 (BOLL, MACD 等)
                                else if (typeof periodValue === 'object') {
                                    periodData.push({
                                        time: timestamp,
                                        value: periodValue.value,
                                        upper: periodValue.upper,
                                        middle: periodValue.middle,
                                        lower: periodValue.lower,
                                        diff: periodValue.diff ?? periodValue.dif,
                                        signal: periodValue.signal ?? periodValue.dea,
                                        histogram: periodValue.histogram ?? periodValue.macd,
                                        k: periodValue.k,
                                        d: periodValue.d,
                                        j: periodValue.j,
                                    });
                                }
                            }
                        });

                        if (periodData.length > 0) {
                            // 按时间升序排序（lightweight-charts 要求）
                            periodData.sort((a, b) => a.time - b.time);
                            result[key] = periodData;
                        }
                    });
                } else {
                    // 旧格式: values 直接包含数值
                    const config = indicatorConfigs[indicatorName as TechnicalIndicator];
                    const periods = config?.periods || [config?.period].filter(Boolean);
                    periods.forEach((period: number) => {
                        const key = `${metricName}_${metricName.toLowerCase()}_${period}`;
                        const mappedData = values.map((v: any) => ({
                            time: v.timestamp || v.time,
                            value: v.value,
                            upper: v.upper,
                            middle: v.middle,
                            lower: v.lower,
                        }));
                        // 按时间升序排序
                        mappedData.sort((a, b) => a.time - b.time);
                        result[key] = mappedData;
                    });
                }
            }
        });

        return result;
    }, [indicatorConfigs]);

    // 转换后的指标数据
    const convertedIndicators = useMemo(() => convertIndicatorData(indicatorData), [indicatorData, convertIndicatorData]);

    // 可见指标状态（用于传递给 LightweightCandlestickChart）
    const visibleIndicators = useMemo(() => {
        const result: Record<string, boolean> = {};
        selectedIndicators.forEach(indicator => {
            const config = indicatorConfigs[indicator];
            if (indicator === 'MACD') {
                const fastPeriod = config?.fastPeriod ?? config?.period ?? 12;
                const slowPeriod = config?.slowPeriod ?? 26;
                const signalPeriod = config?.signalPeriod ?? 9;
                if (fastPeriod && slowPeriod && signalPeriod) {
                    result[`MACD_${fastPeriod}_${slowPeriod}_${signalPeriod}`] = true;
                    return;
                }
            }
            // 优先使用visiblePeriods（用户勾选的周期），空数组表示全部隐藏。
            const periodsToShow = config?.visiblePeriods !== undefined ? config.visiblePeriods : (config?.periods || []);
            periodsToShow.forEach(period => {
                // 生成与 LightweightCandlestickChart 中匹配的 key
                result[`${indicator}_${period}`] = true;
            });
        });
        return result;
    }, [selectedIndicators, indicatorConfigs]);

    // 格式化倒计时显示
    const formatCountdown = useCallback((ms: number): string => {
        if (ms <= 0) return '更新中';

        const totalSeconds = Math.ceil(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const parts: string[] = [];

        if (hours > 0) {
            parts.push(`${hours}h`);
        }
        if (minutes > 0) {
            parts.push(`${minutes}m`);
        }
        if (seconds > 0 || parts.length === 0) {
            parts.push(`${seconds}s`);
        }

        return parts.join(' ');
    }, []);

    // 使用useRef存储fetchCompleteChartData的引用，避免循环依赖
    const fetchCompleteChartDataRef = useRef<(() => Promise<void>) | null>(null);

    // 创建稳定的定时器回调函数
    const handleTimerRefresh = useCallback(async () => {
        // console.log('🔄 [ChartContainer] 定时器触发数据刷新');
        if (fetchCompleteChartDataRef.current) {
            await fetchCompleteChartDataRef.current();
        } else {
            // console.warn('⚠️ [ChartContainer] fetchCompleteChartDataRef.current 为空，无法执行刷新');
        }
    }, []);

    const handleTimerError = useCallback((error: Error) => {
        // console.error('K线图定时更新失败:', error);
        messageApi.error('定时更新失败，请检查网络连接');
    }, []);

    // K线图定时更新Hook - 使用稳定的回调函数
    const {
        isActive: isTimerActive,
        nextUpdateTime,
        toggle: toggleTimer,
        timeUntilNextUpdate
    } = useKlineTimer({
        timeframe: timeFrame,
        onRefresh: handleTimerRefresh,
        enabled: true, // 默认启用定时更新
        onError: handleTimerError
    });

    // 添加时间帧变化的调试日志
    useEffect(() => {
        // console.log(`🔄 [ChartContainer] 时间帧变化: ${timeFrame}`);
    }, [timeFrame]);


    // 统一获取完整图表数据（包含K线数据和技术指标）
    const fetchCompleteChartData = useCallback(async () => {
        if (!instId) {
            // console.log('fetchCompleteChartData: instId is empty');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // 使用独立的K线采样控制
            const currentLimit = klineLimit;

            // console.log('🔄 [fetchCompleteChartData] 开始获取完整图表数据:', {
            //     instId,
            //     timeFrame,
            //     klineLimit,
            //     currentLimit,
            //     selectedIndicators,
            //     note: `klineLimit状态值: ${klineLimit}, 将用于API的limit参数: ${currentLimit}`
            // });

            // 准备请求参数
            const params: any = {
                instId,
                apiKeyId,
                timeframe: timeFrame.toUpperCase(), // 转换为大写格式
                limit: currentLimit
            };

            // 技术指标周期上限验证：最大不能超过60
            const MAX_PERIOD = 60;
            const filterPeriods = (periods: number[] | undefined, indicatorName: string): number[] => {
                if (!periods) return [];
                const filtered = periods.filter(p => p <= MAX_PERIOD);
                const excluded = periods.filter(p => p > MAX_PERIOD);
                if (excluded.length > 0) {
                    // console.warn(`[ChartContainer] ${indicatorName} 过滤超过${MAX_PERIOD}的周期:`, excluded);
                }
                return filtered;
            };

            // 如果有选中的指标，添加到请求参数中
            // 过滤出有效配置的指标（有周期配置的指标）
            const validIndicators = selectedIndicators.filter(indicator => {
                const config = indicatorConfigs[indicator];

                // 检查是否有有效的周期配置
                if (config.visiblePeriods !== undefined) {
                    return config.visiblePeriods.length > 0;
                }

                if (config.periods && config.periods.length > 0) {
                    return true; // 有多周期配置
                }

                // 向后兼容：如果没有多周期但有单周期，且不是RSI（RSI强制多周期）
                if (config.period && indicator !== 'RSI') {
                    return true; // 有单周期配置
                }

                return false; // 无有效配置
            });

            // console.log('[ChartContainer] 过滤后的有效指标:', validIndicators);

            if (validIndicators.length > 0) {
                params.indicators = validIndicators;

                // 辅助函数：获取实际请求的周期（优先使用visiblePeriods）
                const getRequestPeriods = (indicator: TechnicalIndicator): number[] => {
                    const config = indicatorConfigs[indicator];
                    // 优先使用visiblePeriods
                    if (config?.visiblePeriods !== undefined) {
                        return config.visiblePeriods;
                    }
                    // 回退到periods
                    if (config?.periods && config.periods.length > 0) {
                        return config.periods;
                    }
                    // 最后回退到单周期
                    return config?.period ? [config.period] : [];
                };

                // 添加多周期参数（应用60上限过滤，使用visiblePeriods）
                if (validIndicators.includes('EMA')) {
                    const periods = getRequestPeriods('EMA');
                    if (periods.length > 0) {
                        params.emaPeriods = filterPeriods(periods, 'EMA');
                    }
                }
                if (validIndicators.includes('SMA')) {
                    const periods = getRequestPeriods('SMA');
                    if (periods.length > 0) {
                        params.smaPeriods = filterPeriods(periods, 'SMA');
                    }
                }
                if (validIndicators.includes('WMA')) {
                    const periods = getRequestPeriods('WMA');
                    if (periods.length > 0) {
                        params.wmaPeriods = filterPeriods(periods, 'WMA');
                    }
                }
                // RSI强制使用多周期，且必须有有效配置
                if (validIndicators.includes('RSI')) {
                    const periods = getRequestPeriods('RSI');
                    if (periods.length > 0) {
                        params.rsiPeriods = filterPeriods(periods, 'RSI');
                    }
                }
                // BOLL多周期参数：将periods和stdDevs转换为 ["20_2.0", "30_1.5"] 格式
                if (validIndicators.includes('BOLL')) {
                    const periods = getRequestPeriods('BOLL');
                    if (periods.length > 0) {
                        const bollConfig = indicatorConfigs.BOLL;
                        const bollPeriods = filterPeriods(periods, 'BOLL');
                        const bollStdDevs = bollConfig?.stdDevs || [];

                        if (bollPeriods.length > 0) {
                            params.bollParams = bollPeriods.map((period: number, index: number) => {
                                const stdDev = bollStdDevs[index] !== undefined
                                    ? bollStdDevs[index]
                                    : 2.0; // 默认标准差
                                return `${period}_${stdDev}`;
                            });
                        }
                    }
                }
                if (validIndicators.includes('MACD')) {
                    const macdConfig = indicatorConfigs.MACD;
                    const fastPeriod = macdConfig?.fastPeriod ?? macdConfig?.period ?? 12;
                    const slowPeriod = macdConfig?.slowPeriod ?? 26;
                    const signalPeriod = macdConfig?.signalPeriod ?? 9;
                    params.macdPeriods = [fastPeriod, slowPeriod, signalPeriod];
                }
                // KDJ
                if (validIndicators.includes('KDJ')) {
                    const periods = getRequestPeriods('KDJ');
                    if (periods.length > 0) {
                        params.kdjPeriods = filterPeriods(periods, 'KDJ');
                    }
                }
                // CCI
                if (validIndicators.includes('CCI')) {
                    const periods = getRequestPeriods('CCI');
                    if (periods.length > 0) {
                        params.cciPeriods = filterPeriods(periods, 'CCI');
                    }
                }
                // ATR
                if (validIndicators.includes('ATR')) {
                    const periods = getRequestPeriods('ATR');
                    if (periods.length > 0) {
                        params.atrPeriods = filterPeriods(periods, 'ATR');
                    }
                }
                // OBV
                if (validIndicators.includes('OBV')) {
                    const periods = getRequestPeriods('OBV');
                    if (periods.length > 0) {
                        params.obvPeriods = filterPeriods(periods, 'OBV');
                    }
                }
                // ADX
                if (validIndicators.includes('ADX')) {
                    const periods = getRequestPeriods('ADX');
                    if (periods.length > 0) {
                        params.adxPeriods = filterPeriods(periods, 'ADX');
                    }
                }
            }

            // console.log('[ChartContainer] 发送API请求参数:', params);

            // 调用新的统一接口
            const response: CompleteChartData = await tradingService.getCompleteChartData(params);
            // console.log('获取到的完整图表数据:', response);

            if (response && response.candles && response.candles.length > 0) {
                // 转换K线数据格式为 KLineBar
                const klineData: KLineBar[] = response.candles.map((candle: MarkPriceCandle) => ({
                    time: candle.timestamp,
                    open: candle.open,
                    high: candle.high,
                    low: candle.low,
                    close: candle.close,
                    volume: Number(candle.volume || (candle as any).vol || candle.volumeCcy || (candle as any).volCcy || candle.volCcyQuote || (candle as any).volCcyQuote || 0),
                    confirmed: candle.confirm === 1,
                }));

                // 按时间排序
                klineData.sort((a, b) => a.time - b.time);
                setData(klineData);

                // 设置当前标记价格（从getCompleteChartData获取）
                if (response.markPrice) {
                    setCurrentMarkPrice(response.markPrice);
                    // console.log('设置当前标记价格:', response.markPrice);
                }

                // 处理技术指标数据
                if (response.indicators) {
                    const newIndicatorData: typeof indicatorData = {};

                    Object.keys(response.indicators).forEach(indicator => {
                        const indicatorData = response.indicators![indicator];
                        if (indicatorData && indicatorData.values) {
                            newIndicatorData[indicator as keyof typeof newIndicatorData] = {
                                success: true,
                                message: '',
                                data: indicatorData
                            };
                            // console.log(`${indicator}指标数据处理成功:`, indicatorData);
                        }
                    });

                    setIndicatorData(newIndicatorData);
                }
            } else {
                // console.log('完整图表数据为空或失败:', response);
                setError('未获取到数据');
                messageApi.error('获取图表数据失败');
            }
        } catch (err) {
            // console.error('获取完整图表数据失败:', err);
            setError('获取图表数据失败');
            messageApi.error('获取图表数据失败');
        } finally {
            setLoading(false);
        }
    }, [instId, apiKeyId, timeFrame, klineLimit, selectedIndicators, indicatorConfigs]);

    // 更新fetchCompleteChartData的引用
    fetchCompleteChartDataRef.current = fetchCompleteChartData;

    // 初始化和数据更新
    useEffect(() => {
        fetchCompleteChartData();
    }, [fetchCompleteChartData]);

    // 监听外部markPrice变化（来自TradingForm的价格更新）
    useEffect(() => {
        if (markPrice && markPrice !== currentMarkPrice) {
            // console.log('🔄 [ChartContainer] 接收到外部价格更新:', markPrice);
            setCurrentMarkPrice(markPrice);
        }
    }, [markPrice, currentMarkPrice]);

    // 处理周期切换
    const handlePeriodChange = (newTimeFrame: string) => {
        updateTimeFrame(newTimeFrame as any);
    };

    // 处理K线采样变化
    const handleKlineLimitChange = (newLimit: number) => {
        // console.log(`🎯 [ChartContainer] 采样按钮点击，更新klineLimit: ${newLimit}`);
        updateKlineLimit(newLimit as any);
    };

    // 处理指标激活
    const handleActivateIndicator = (indicator: TechnicalIndicator) => {
        // console.log(`[ChartContainer] 激活指标 ${indicator}`);
        toggleIndicator(indicator);
    };

    // 创建精确的指标配置依赖，确保多周期数组变化能被检测到
    const indicatorConfigsString = useMemo(() => {
        try {
            // 创建一个稳定的排序对象来避免序列化时的顺序问题
            // 包含visiblePeriods，因为可见性变化需要触发数据重新获取
            const stableConfigs: any = {};
            Object.keys(indicatorConfigs).sort().forEach(key => {
                const config = indicatorConfigs[key as TechnicalIndicator];
                stableConfigs[key] = {
                    period: config.period,
                    periods: config.periods ? [...config.periods].sort((a, b) => a - b) : undefined,
                    visiblePeriods: config.visiblePeriods ? [...config.visiblePeriods].sort((a, b) => a - b) : undefined,
                    periodColors: config.periodColors,
                    stdDev: config.stdDev,
                    stdDevs: config.stdDevs,
                    fastPeriod: config.fastPeriod,
                    slowPeriod: config.slowPeriod,
                    signalPeriod: config.signalPeriod
                };
            });
            const result = JSON.stringify(stableConfigs);

            // 添加调试信息追踪配置变更
            // console.log(`[ChartContainer] indicatorConfigsString 更新 (排除visiblePeriods):`, {
            //     字符串长度: result.length,
            //     指标数量: Object.keys(stableConfigs).length
            // });

            return result;
        } catch (error) {
            // console.error('[ChartContainer] indicatorConfigsString 序列化错误:', error);
            return JSON.stringify({}); // 返回空对象字符串作为fallback
        }
    }, [indicatorConfigs]);

    // 创建选中指标的依赖字符串
    const selectedIndicatorsString = useMemo(() => {
        try {
            return JSON.stringify([...selectedIndicators].sort()); // 排序确保顺序一致
        } catch (error) {
            // console.error('[ChartContainer] selectedIndicatorsString 序列化错误:', error);
            return JSON.stringify([]); // 返回空数组字符串作为fallback
        }
    }, [selectedIndicators]);

    // 调试函数：打印当前技术指标状态
    const debugIndicatorState = useCallback(() => {
        console.group('🔍 [ChartContainer] 技术指标状态调试');
        // console.log('选中的指标:', selectedIndicators);
        // console.log('指标配置详情:', indicatorConfigs);
        // console.log('指标配置字符串:', indicatorConfigsString ? indicatorConfigsString.substring(0, 200) + '...' : 'undefined');
        // console.log('指标数据状态:', Object.keys(indicatorData));

        // 检查每个选中指标的配置
        selectedIndicators.forEach(indicator => {
            const config = indicatorConfigs[indicator];
            const hasMultiPeriod = config.periods && config.periods.length > 0;
            // console.log(`📊 ${indicator} 指标:`, {
            //     基础周期: config.period,
            //     多周期: config.periods,
            //     多周期数量: config.periods?.length || 0,
            //     颜色配置: config.periodColors,
            //     标准差: config.stdDev,
            //     是否多周期模式: hasMultiPeriod,
            //     是否有数据: !!indicatorData[indicator],
            //     数据结构: indicatorData[indicator] ? Object.keys(indicatorData[indicator]) : []
            // });

            // 特别检查多周期配置
            if (hasMultiPeriod && config.periods) {
                // console.log(`🔢 ${indicator} 多周期详情:`, {
                //     周期列表: config.periods,
                //     对应颜色: config.periodColors,
                //     周期颜色映射: config.periods.map((period, index) => ({
                //         period,
                //         color: config.periodColors?.[index]
                //     }))
                // });
            }
        });

        // console.log('✨ 配置变更追踪信息:', {
        //     选中指标数量: selectedIndicators.length,
        //     配置字符串长度: indicatorConfigsString ? indicatorConfigsString.length : 0,
        //     数据状态: indicatorData ? '有效' : '无'
        // });

        console.groupEnd();
    }, [selectedIndicatorsString, indicatorConfigsString, fetchCompleteChartData]);

    // 在开发环境中，将调试函数暴露到全局
    if (false) { // 禁用开发环境调试功能
        (window as any).debugChartIndicator = debugIndicatorState;

        // 添加手动强制更新函数
        (window as any).forceChartUpdate = () => {
            // console.log('🔄 [ChartContainer] 手动强制触发图表更新');
            fetchCompleteChartData();
        };

        // 添加配置比较函数
        (window as any).compareIndicatorConfigs = () => {
            console.group('🔍 配置比较分析');
            // console.log('当前配置:', indicatorConfigs);
            // console.log('配置字符串:', indicatorConfigsString);
            // console.log('字符串长度:', indicatorConfigsString.length);
            // console.log('选中指标:', selectedIndicators);
            // console.log('指标数据:', Object.keys(indicatorData));
            console.groupEnd();
        };

        // 添加配置更新模拟器
        (window as any).simulateConfigUpdate = (indicator: string, newPeriods: number[]) => {
            // console.log(`🧪 [ChartContainer] 模拟配置更新 ${indicator}:`, newPeriods);
            // 直接调用组件的updateIndicatorConfig函数
            updateIndicatorConfig(indicator as any, {
                periods: newPeriods,
                periodColors: newPeriods.map((_, index) => ['#F0B429', '#00d2d3', '#00d97e'][index % 3])
            });
        };
    }

    // 监听指标选择和参数变化，自动重新获取数据（使用统一的fetchCompleteChartData）
    useEffect(() => {
        // console.log('🔄 [ChartContainer] 指标配置变化触发重新获取数据', {
        //     selectedIndicators,
        //     selectedIndicatorsCount: selectedIndicators.length,
        //     indicatorConfigsKeys: Object.keys(indicatorConfigs),
        //     indicatorConfigsString: indicatorConfigsString ? indicatorConfigsString.substring(0, 200) + '...' : 'undefined',
        //     changeDetected: true,
        //     详细配置: indicatorConfigs
        // });

        // 自动调用调试函数（开发环境）
        if (false) { // 禁用开发环境调试功能
            debugIndicatorState();
        }

        // 立即清理不在选中列表中的指标数据
        setIndicatorData(prevData => {
            const newData: typeof indicatorData = {};
            const removedIndicators: string[] = [];

            // 找出需要保留的指标和需要移除的指标
            Object.keys(prevData).forEach(indicator => {
                if (selectedIndicators.includes(indicator as any)) {
                    newData[indicator as keyof typeof newData] = prevData[indicator as keyof typeof prevData];
                } else {
                    removedIndicators.push(indicator);
                }
            });

            if (removedIndicators.length > 0) {
                // console.log('[ChartContainer] 清理指标数据:', removedIndicators);
            }

            return newData;
        });

        // 使用更短的防抖延迟，提高配置变更的响应速度
        const timeoutId = setTimeout(() => {
            // console.log('[ChartContainer] 执行数据重新获取 - 配置已变更');
            fetchCompleteChartData();
        }, 50); // 减少延迟到50ms，提供更好的用户体验

        return () => {
            clearTimeout(timeoutId);
            // console.log('[ChartContainer] 清理防抖定时器');
        };
    }, [selectedIndicatorsString, indicatorConfigsString, fetchCompleteChartData]); // 使用字符串化的依赖确保变化检测

    // ✅ [ENABLED] 2025-11-21: 启用基于时间帧的智能定时更新
    // 替换原有的固定间隔定时器，使用useKlineTimer Hook管理
    // 时间帧变更时自动切换更新间隔，支持暂停/恢复和错误处理
    // 旧的自动刷新代码已被移除（第838-886行）

    // 监听容器大小变化
    useEffect(() => {
        const updateWidth = () => {
            const container = document.getElementById(`chart-container-${instId}`);
            if (container) {
                setContainerWidth(container.clientWidth);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);

        return () => window.removeEventListener('resize', updateWidth);
    }, [instId]);

    // 手动刷新
    const handleRefresh = () => {
        fetchCompleteChartData();
    };

    // 打开全屏模态框
    const handleOpenFullscreen = () => {
        setIsFullscreenOpen(true);
    };

    // 关闭全屏模态框
    const handleCloseFullscreen = () => {
        setIsFullscreenOpen(false);
    };


    return (
        <div
            id={`chart-container-${instId}`}
            style={{
                width: '100%',
                marginBottom: '16px',
                transition: 'all 0.3s ease',
            }}
        >
            <Card
                title={
                    <Space>
                        <LineChartOutlined/>
                        <span>{instId} K线图 🔥MODIFIED🔥</span>
                    </Space>
                }
                extra={
                    <Space size="small">
                        {/* 定时器控制按钮 */}
                        <Tooltip title={isTimerActive ? '暂停自动更新' : '恢复自动更新'}>
                            <Button
                                icon={isTimerActive ? <PauseCircleOutlined/> : <PlayCircleOutlined/>}
                                onClick={toggleTimer}
                                size="small"
                                type={isTimerActive ? 'default' : 'primary'}
                                style={{
                                    backgroundColor: isTimerActive ? 'var(--pt-bg-hover)' : '#F0B429',
                                    borderColor: isTimerActive ? 'var(--pt-border-bright)' : '#F0B429',
                                    color: isTimerActive ? '#fff' : '#fff'
                                }}
                            />
                        </Tooltip>

                        {/* 手动刷新按钮 */}
                        <Tooltip title="立即刷新">
                            <Button
                                icon={<ReloadOutlined/>}
                                onClick={handleRefresh}
                                loading={loading}
                                size="small"
                                style={{
                                    backgroundColor: 'var(--pt-bg-hover)',
                                    borderColor: 'var(--pt-border-bright)',
                                    color: '#fff'
                                }}
                            />
                        </Tooltip>
                    </Space>
                }
                size="small"
                style={{
                    backgroundColor: 'var(--pt-bg-panel)',
                    borderColor: 'var(--pt-border)',
                    width: '100%'
                }}
                styles={{
                    header: {
                        backgroundColor: 'var(--pt-bg-hover)',
                        borderColor: 'var(--pt-border)',
                        color: '#fff'
                    },
                    body: {
                        backgroundColor: 'var(--pt-bg-panel)',
                        padding: '8px 16px 16px 16px' // 顶部8px，给控制面板留空间
                    }
                }}
            >
                <Spin spinning={loading}>
                    {error ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: height,
                            color: '#ff5252',
                            textAlign: 'center'
                        }}>
                            <div>
                                <div style={{fontSize: '16px', marginBottom: '8px'}}>❌</div>
                                <div>{error}</div>
                                <Button
                                    type="link"
                                    onClick={handleRefresh}
                                    style={{color: '#F0B429', padding: 0, marginTop: '8px'}}
                                >
                                    点击重试
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div style={{width: '100%', height: '100%', position: 'relative'}}>
                            {/* 控制面板 - 两行统一工具栏 */}
                            <div className="chart-controls">
                                <div className="chart-toolbar">
                                    {/* 第一行：时间帧 + 采样 + 状态 */}
                                    <div className="toolbar-row toolbar-row-primary">
                                        <div className="toolbar-fullscreen">
                                            <Button
                                                icon={<FullscreenOutlined/>}
                                                onClick={handleOpenFullscreen}
                                                size="small"
                                                title="全屏查看"
                                                className="fullscreen-btn"
                                            />
                                        </div>

                                        <div className="toolbar-separator-vertical"/>

                                        <div className="toolbar-group">
                                            <span className="toolbar-label">时间:</span>
                                            <Segmented
                                                size="small"
                                                value={timeFrame}
                                                options={TIME_PERIODS.map(p => ({
                                                    label: p.label,
                                                    value: p.value,
                                                    disabled: p.value === timeFrame
                                                }))}
                                                onChange={(value) => handlePeriodChange(value as string)}
                                            />
                                        </div>

                                        <div className="toolbar-group">
                                            <span className="toolbar-label">采样:</span>
                                            <Segmented
                                                size="small"
                                                value={klineLimit}
                                                options={KLINE_SAMPLE_OPTIONS.map(o => ({
                                                    label: o.label,
                                                    value: o.value,
                                                    disabled: o.value === klineLimit
                                                }))}
                                                onChange={(value) => handleKlineLimitChange(value as number)}
                                            />
                                        </div>

                                        <div className="toolbar-separator-vertical"/>

                                        <div className="toolbar-status">
                                            <span
                                                className={`status-dot ${isTimerActive ? 'active' : 'inactive'}`}
                                                title={isTimerActive ? '定时更新已启用' : '定时更新已暂停'}
                                                onClick={toggleTimer}
                                            />
                                            <span className="status-countdown">
                                                {formatCountdown(timeUntilNextUpdate)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* 第二行：技术指标 */}
                                    <div className="toolbar-row toolbar-row-secondary">
                                        <CompactIndicatorBar
                                            selectedIndicators={selectedIndicators}
                                            indicatorConfigs={indicatorConfigs}
                                            onToggleIndicator={toggleIndicator}
                                            onUpdateIndicatorConfig={updateIndicatorConfig}
                                            onClearAllIndicators={clearAllIndicators}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* K线图 */}
                            <div style={{width: '100%', height: height - 90, position: 'relative'}}>
                                <div className="chart-price-overlay">
                                    <span className={`price-value ${mainIsPriceUp ? 'up' : 'down'}`}>
                                        {mainDisplayPrice != null ? mainDisplayPrice.toFixed(2) : '--'}
                                    </span>
                                    {mainPriceChangePercent != null && (
                                        <span className={`price-change ${mainPriceChangePercent >= 0 ? 'up' : 'down'}`}>
                                            {mainPriceChangePercent >= 0 ? '+' : ''}{mainPriceChangePercent.toFixed(2)}%
                                        </span>
                                    )}
                                </div>
                                <LightweightCandlestickChart
                                    data={data}
                                    height={height - 90}
                                    loading={loading}
                                    markPrice={currentMarkPrice || markPrice}
                                    timeFrame={timeFrame}
                                    indicators={convertedIndicators}
                                    visibleIndicators={visibleIndicators}
                                    maxVisibleBars={klineLimit}
                                />
                            </div>
                        </div>
                    )}
                </Spin>
            </Card>

            {/* 全屏图表模态框 */}
            <FullscreenChartModal
                visible={isFullscreenOpen}
                onClose={handleCloseFullscreen}
                instId={instId}
                apiKeyId={apiKeyId}
                data={data}
                loading={loading}
                _error={error}
                timeFrame={timeFrame as any}
                klineLimit={klineLimit}
                selectedIndicators={selectedIndicators}
                indicatorConfigs={indicatorConfigs}
                convertedIndicators={convertedIndicators}
                visibleIndicators={visibleIndicators}
                markPrice={markPrice}
                currentMarkPrice={currentMarkPrice}
                fourHourAvgChange={fourHourAvgChange}
                // 定时器状态
                isTimerActive={isTimerActive}
                nextUpdateTime={nextUpdateTime}
                timeUntilNextUpdate={timeUntilNextUpdate}
                onToggleTimer={toggleTimer}
                // 格式化函数
                formatTimeframeDisplay={formatTimeframeDisplay}
                formatCountdown={formatCountdown}
                onPeriodChange={handlePeriodChange}
                onKlineLimitChange={handleKlineLimitChange}
                onToggleIndicator={toggleIndicator}
                onUpdateIndicatorConfig={updateIndicatorConfig}
                onActivateIndicator={handleActivateIndicator}
                onClearAllIndicators={clearAllIndicators}
                readOnly={readOnly}
            />
        </div>
    );
};

const MemoizedChartContainer = memo(ChartContainer);
export default MemoizedChartContainer;// Force reload
